import React from 'react';
import { getEnumValues } from '../../../../../../../components/helpers/helpers';
import { AircraftThresholdMergeResult } from '../../../../../../../monitoring/merger/aircraft-threshold-merger';
import { AirportThresholdMergeResult } from '../../../../../../../monitoring/merger/airport-threshold-merger';
import { ThresholdEnableStateOptions } from '../../../../../../../types/dropdown-options';
import {
    AircraftThresholdType,
    AirportThresholdType,
    CompanyThresholdsMode,
    ThresholdEnableState,
    ThresholdInfo,
    ThresholdLevel
} from '../../../../../../../types/thresholds';
import { DropdownListWithValueField } from '../../../../../../components/inputs/DropdownListWithValueField';
import { SliderWithTooltip } from '../../../../../../components/inputs/SliderWithTooltip';
import { AircraftAlertCell, AirportAlertCell, AlertClickArgs } from './AlertView';
import { SettingsThresholdsLevel as Level, ThresholdLevelClickedArgs } from './SettingsThresholdsLevel';
import './ThresholdsGirdRow.css';

const enableStateCssClass: Record<ThresholdEnableState, string> = {
    [ThresholdEnableState.notSet]: '',
    [ThresholdEnableState.enabled]: 'enabled',
    [ThresholdEnableState.disabled]: 'disabled'
};

export type AircraftThresholdLevelClickedArgs = ThresholdLevelClickedArgs & { type: AircraftThresholdType };
export type AirportThresholdLevelClickedArgs = ThresholdLevelClickedArgs & { type: AirportThresholdType };

export type AircraftThresholdAlertClickedArgs = AlertClickArgs & { type: AircraftThresholdType };
export type AirportThresholdAlertClickedArgs = AlertClickArgs & { type: AirportThresholdType };

type LevelClickArgsFor<T> = T extends AircraftThresholdType ? AircraftThresholdLevelClickedArgs : AirportThresholdLevelClickedArgs;
type AlertClickArgsFor<T> = T extends AircraftThresholdType ? AircraftThresholdAlertClickedArgs : AirportThresholdAlertClickedArgs;

type MergeResultFor<T> = T extends AircraftThresholdType ? AircraftThresholdMergeResult : AirportThresholdMergeResult;
const allLevels = getEnumValues(ThresholdLevel);

function createRowComponent<T extends AircraftThresholdType | AirportThresholdType>(
    AlertViewComponent: typeof AirportAlertCell | typeof AircraftAlertCell
) {
    return (props: {
        type: T;
        companyMode: CompanyThresholdsMode;
        context: MergeResultFor<T>;
        readonly: boolean;
        definitions: Record<T, ThresholdInfo>;
        onPriorityChange?: (type: T, value: number) => void;
        onEnableChange?: (type: T, value: ThresholdEnableState) => void;
        onLevelClicked?: (args: LevelClickArgsFor<T>) => void;
        onAlertsClicked?: (args: AlertClickArgsFor<T>) => void;
    }) => {
        const { type, context: context, onLevelClicked, onEnableChange, onPriorityChange, onAlertsClicked } = props;
        const info = props.definitions[type];

        const handleLevelOnClick = React.useCallback(
            (e: ThresholdLevelClickedArgs) => {
                onLevelClicked?.({ ...e, type } as LevelClickArgsFor<T>);
            },
            [onLevelClicked, type]
        );

        const handleAlertsOnClick = React.useCallback(
            (e: AlertClickArgs) => {
                props.onAlertsClicked?.({
                    ...e,
                    type
                } as AlertClickArgsFor<T>);
            },
            [onAlertsClicked]
        );

        const mode = props.companyMode;
        const disableEnableColumn =
            mode === CompanyThresholdsMode.minimum && props.context.inherited?.enableState === ThresholdEnableState.enabled;

        return (
            <>
                <div className="d-flex align-items-center gap-2 ps-2">
                    <div
                        className={
                            'threshold-enable-indicator flex-shrink-0 ' +
                            enableStateCssClass[context.effective?.enableState ?? ThresholdEnableState.notSet]
                        }></div>

                    <DropdownListWithValueField
                        className="fe-dropdown-small"
                        disabled={disableEnableColumn || props.readonly}
                        value={context.definition?.enableState ?? ThresholdEnableState.notSet}
                        data={ThresholdEnableStateOptions}
                        onChange={(e) => onEnableChange?.(type, e.value)}
                    />
                </div>
                <div className="d-flex align-items-center">{info.name}</div>
                <div className="d-flex align-items-center">
                    {props.readonly ? (
                        <div>{context.effective?.priority ?? 50}</div>
                    ) : (
                        <>
                            <SliderWithTooltip
                                className="w-100"
                                min={0}
                                max={100}
                                step={5}
                                largeStep={5}
                                value={context?.effective?.priority ?? 50}
                                onChange={(e: { value: number }) => onPriorityChange?.(type, Math.floor(e.value / 5) * 5)}
                            />
                        </>
                    )}
                </div>
                {info.alertsOnly ? (
                    <>
                        <div className="cursor-not-allowed"></div>
                        <div className="cursor-not-allowed"></div>
                        <div className="cursor-not-allowed"></div>
                        <div className="cursor-not-allowed"></div>
                        <div className="cursor-not-allowed"></div>
                    </>
                ) : (
                    allLevels.map((l) => (
                        <Level
                            key={l}
                            readonly={props.readonly}
                            info={info}
                            mode={mode}
                            context={context}
                            onClick={handleLevelOnClick}
                            level={l}
                        />
                    ))
                )}
                <AlertViewComponent readonly={props.readonly} onClick={handleAlertsOnClick} alerts={context.effective?.alerts} />
            </>
        );
    };
}

export const AircraftThresholdsGridRow = React.memo(createRowComponent<AircraftThresholdType>(AircraftAlertCell));
export const AirportThresholdsGridRow = React.memo(createRowComponent<AirportThresholdType>(AirportAlertCell));
